import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import "./ConfirmDialog.css";
import "../../styles/buttonColors.css";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Button, Box, Typography } from "@mui/material";

const TYPE_COLORS = {
  info: "#2196f3",
  error: "#f44336",
  warning: "#ff9800",
  success: "#4caf50",
};

const TYPE_ICONS = {
  info: (
    <InfoOutlineIcon sx={{ color: "#ffffff", width: "48px", height: "48px" }} />
  ),
  error: (
    <ReportGmailerrorredIcon
      sx={{ color: "#ffffff", width: "48px", height: "48px" }}
    />
  ),
  warning: (
    <WarningAmberIcon
      sx={{ color: "#ffffff", width: "48px", height: "48px" }}
    />
  ),
  success: (
    <CheckCircleOutlineIcon
      sx={{ color: "#ffffff", width: "48px", height: "48px" }}
    />
  ),
};

/**
 * Show a custom confirmation dialog.
 * @param {Object} params
 * @param {'info'|'error'|'warning'|'success'} [params.type] - Dialog type for top border color.
 * @param {React.ReactNode} [params.icon] - Icon or image to show on the left.
 * @param {string} [params.title] - Title text.
 * @param {string} [params.description] - Description text.
 * @param {Array} [params.buttons] - Array of button objects { label, onClick, className, style }
 * @param {number} [params.zIndex] - Custom z-index for the dialog overlay
 * @param {string|number} [params.maxWidth] - Custom max width for the dialog (e.g., '800px', 800, '80%')
 */
function ConfirmDialog({
  type = "info",
  icon = null,
  title = "Are you sure?",
  description = "",
  buttons = [],
  zIndex = 1300,
  maxWidth = "500px",
}) {
  const dialogMaxWidth = typeof maxWidth === "number" ? `${maxWidth}px` : (maxWidth || "500px");

  confirmAlert({
    customUI: ({ onClose }) => {
      const isDarkMode =
        document.documentElement.getAttribute("data-theme") === "dark";

      return (
        <Box
          sx={{
            backgroundColor: isDarkMode ? "#1e2832" : "#ffffff",
            borderRadius: { xs: "12px", sm: "16px" },
            padding: 0,
            width: "100%",
            minWidth: { xs: 0, sm: "440px" },
            maxWidth: { xs: "calc(100vw - 24px)", sm: dialogMaxWidth },
            maxHeight: { xs: "calc(100vh - 48px)", sm: "none" },
            display: "flex",
            flexDirection: "column",
            boxShadow: isDarkMode
              ? "0 20px 60px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 20px 60px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05)",
            overflow: "hidden",
            fontFamily: '"Poppins", sans-serif',
            animation: "dialogSlideIn 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
            "@keyframes dialogSlideIn": {
              "0%": {
                opacity: 0,
                transform: "translateY(-20px) scale(0.95)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
            },
          }}
        >
          {/* Header Section with Icon */}
          <Box
            sx={{
              backgroundColor: TYPE_COLORS[type],
              padding: { xs: "20px 16px 16px", sm: "32px 32px 24px" },
              textAlign: "center",
              position: "relative",
              flexShrink: 0,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: `linear-gradient(90deg, transparent 0%, ${TYPE_COLORS[type]} 50%, transparent 100%)`,
                opacity: 0.4,
              },
            }}
          >
            {/* Icon Container */}
            <Box
              sx={{
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                width: { xs: "60px", sm: "80px" },
                height: { xs: "60px", sm: "80px" },
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                border: "3px solid rgba(255, 255, 255, 0.3)",
                mb: { xs: 1.5, sm: 2 },
              }}
            >
              {icon || TYPE_ICONS[type]}
            </Box>

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                color: "#ffffff",
                fontWeight: 600,
                fontSize: { xs: "1.15rem", sm: "1.5rem" },
                fontFamily: '"Poppins", sans-serif',
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Content Section */}
          <Box
            sx={{
              padding: { xs: "16px 16px 20px", sm: "28px 32px 32px" },
              textAlign: "center",
              overflowY: "auto",
              flex: 1,
            }}
          >
            {/* Description */}
            {typeof description === "function" ? (
              description(onClose)
            ) : typeof description === "string" ? (
              <Typography
                variant="body1"
                sx={{
                  color: isDarkMode
                    ? "rgba(255, 255, 255, 0.8)"
                    : "rgba(0, 0, 0, 0.7)",
                  fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "0.9375rem" },
                  lineHeight: 1.7,
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 400,
                  mb: 4,
                }}
              >
                {description}
              </Typography>
            ) : (
              <Box sx={{ mb: 4 }}>{description}</Box>
            )}

            {/* Actions Section */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
              }}
            >
              {buttons.map((btn, idx) => (
                <Button
                  key={idx}
                  className={`custom-confirm-dialog__btn ${btn.className || ""
                    }`}
                  onClick={() => {
                    btn.onClick && btn.onClick();
                    onClose();
                  }}
                  style={btn.style}
                  variant={
                    idx === buttons.length - 1 ? "contained" : "outlined"
                  }
                  sx={{
                    minWidth: "130px",
                    px: 3.5,
                    py: 1.25,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontSize: "15px",
                    fontWeight: 600,
                    fontFamily: '"Poppins", sans-serif',
                    transition: "all 0.2s ease",
                    ...(idx === buttons.length - 1
                      ? {
                        backgroundColor: TYPE_COLORS[type],
                        color: "#ffffff",
                        boxShadow: `0 4px 12px ${TYPE_COLORS[type]}40`,
                        "&:hover": {
                          backgroundColor: TYPE_COLORS[type],
                          filter: "brightness(1.1)",
                          boxShadow: `0 6px 16px ${TYPE_COLORS[type]}50`,
                          transform: "translateY(-1px)",
                        },
                      }
                      : {
                        borderColor: isDarkMode
                          ? "rgba(255, 255, 255, 0.2)"
                          : "rgba(0, 0, 0, 0.2)",
                        color: isDarkMode
                          ? "rgba(255, 255, 255, 0.9)"
                          : "rgba(0, 0, 0, 0.8)",
                        "&:hover": {
                          backgroundColor: isDarkMode
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.05)",
                          borderColor: isDarkMode
                            ? "rgba(255, 255, 255, 0.3)"
                            : "rgba(0, 0, 0, 0.3)",
                        },
                      }),
                  }}
                >
                  {btn.label}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      );
    },
    closeOnEscape: true,
    closeOnClickOutside: true,
    overlayClassName: `react-confirm-alert-overlay-z${zIndex}`,
  });

  // Apply custom z-index to the overlay
  if (zIndex !== 1300) {
    setTimeout(() => {
      const overlay = document.querySelector(
        `.react-confirm-alert-overlay-z${zIndex}`
      );
      if (overlay) {
        overlay.style.zIndex = zIndex;
      }
    }, 0);
  }
}

export default ConfirmDialog;
